import { ReactNode, useMemo } from 'react'
import { useCharacterClasses } from 'app/frontend/base/hooks/useCharacterClasses'
import { TeamEventParticipantsValueProvider } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/context/TeamEventParticipantsContext'
import { TeamEventPlanningInfoProvider } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/context/TeamEventPlanningInfoContext'
import {
  TeamEventUpdateProvider,
  TeamEventUpdateContextValue,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/context/TeamEventUpdateContext'
import { EventActivityParticipantEntry } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/eventActivityParticipantEntry'
import { resolveEventActivityIssues } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/eventActivityIssues'

type Props = {
  activities: wowaudit.EventActivity[]
  entries: EventActivityParticipantEntry[]
  onUpdateSelection: TeamEventUpdateContextValue['updateSelection']
  onUpdateActivityOrder?: TeamEventUpdateContextValue['updateEventActivityOrder']
  raidsPlanningScope?: wowaudit.Event['raidsPlanningScope']
  children: ReactNode
}

const noopAsync = async () => undefined
const noop = () => undefined

const DefaultSetupProviders = ({
  activities,
  entries,
  onUpdateSelection,
  onUpdateActivityOrder,
  raidsPlanningScope,
  children,
}: Props) => {
  const characterClasses = useCharacterClasses()
  const activityIssuesById = useMemo(() => {
    const issuesById = new Map<
      number,
      ReturnType<typeof resolveEventActivityIssues>
    >()
    activities.forEach((activity) => {
      issuesById.set(
        activity.id,
        resolveEventActivityIssues({
          activity,
          entries,
          unavailableStatuses: [],
          characterClasses,
        }),
      )
    })
    return issuesById
  }, [activities, characterClasses, entries])

  const uniqueInstances = useMemo(() => {
    const instancesById = new Map<number, wowaudit.Instance>()
    activities.forEach((activity) => {
      if (activity.instance) {
        instancesById.set(activity.instance.id, activity.instance)
      }
    })
    return Array.from(instancesById.values())
  }, [activities])

  const primaryActivityId = activities[0]?.id ?? 0
  const resolvedPlanningScope =
    raidsPlanningScope ?? (activities.length > 1 ? 'per_encounter' : 'single')

  const participantsValue = useMemo(
    () => ({
      event: {
        id: 0,
        activities,
        activityType: 'raids' as const,
        autoAddParticipants: true,
        date: '',
        effectiveGuestPolicy: 'disabled',
        endsAt: '',
        finishTime: '',
        instances: uniqueInstances,
        optional: false,
        participantReferences: [],
        participants: entries.map((entry) => entry.participant),
        path: '',
        primaryActivityId,
        raidsPlanningScope: resolvedPlanningScope,
        siblings: {},
        startTime: '',
        startsAt: '',
        status: 'planned' as const,
        touched: false,
      } as wowaudit.Event,
      allEntries: entries,
      activityIssuesById,
      activeActivityIds: new Set(activities.map((activity) => activity.id)),
      planningInfoByActivityAndCharacter: new Map(),
      characterDetailsById: {},
    }),
    [
      activities,
      activityIssuesById,
      entries,
      primaryActivityId,
      resolvedPlanningScope,
      uniqueInstances,
    ],
  )

  const updateValue = useMemo<TeamEventUpdateContextValue>(
    () => ({
      isSaving: false,
      editEvent: noopAsync,
      updateEvent: noopAsync,
      updateStatus: noop,
      updateAdminComment: noop,
      updateSelection: onUpdateSelection,
      updateActivitySelectionForAllParticipants: noop,
      updateSelectionForAllActivities: noop,
      updateEventActivity: noopAsync,
      copySelectionsFromActivity: noopAsync,
      exportEventActivityOverrides: noopAsync,
      updateEventActivityOrder: onUpdateActivityOrder ?? noopAsync,
      addEventActivities: noopAsync,
      removeEventActivity: noopAsync,
      importEventFromPreviousEvent: noopAsync,
      resetAllSelections: noopAsync,
      setAllActivitiesActive: noopAsync,
      removeGuest: noop,
    }),
    [onUpdateActivityOrder, onUpdateSelection],
  )

  return (
    <TeamEventParticipantsValueProvider value={participantsValue}>
      <TeamEventPlanningInfoProvider
        value={{
          showPlanningInfo: false,
          setShowPlanningInfo: noop,
        }}
      >
        <TeamEventUpdateProvider value={updateValue}>
          {children}
        </TeamEventUpdateProvider>
      </TeamEventPlanningInfoProvider>
    </TeamEventParticipantsValueProvider>
  )
}

export default DefaultSetupProviders
